import { generateCoordFunctions, sortRowsArray, } from "@tedivo/tedivo-bay-grid-pure";
import { BayLevelEnum, CraneSideEnum, VesselPartTypeEnum, } from "open-vessel-definition";
import { SetEx, createDictionaryMultiple, feetToMillimeters, roundDec, } from "@tedivo/tedivo-pure-helpers";
import { colorShade } from "../helpers/colorShade";
import { createSvgTopBayNumbers } from "../helpers/createSvgTopBayNumbers";
import { generateSideContainerSymbols } from "./generateSideContainerSymbols";
export const SVG_WIDTH = 1500;
export const DISTANCE_TO_CONTAINER_FOR_SHIP_LINE = 4000;
export function useSimpleSideView({ prefixSymbol = "smbCn", sizeSummary, baysData, lcgVcgTcgAndPairings, symbolsOptions, vesselPartsData = [], containersWithPositions, selectContainerCriteria, svgElement, svgElementMeasures, bayNumbersDoubleClickAction, }) {
    const { bayLevelPositionsAbove, bayLevelPositionsBelow, missingImportantVcgs, missingImportantLcgs, missingImportantTcgs, cgStats: { totalSlotsCount }, lcgsBy20Bay, pairedBaysCalc: pairedBays, minTierAbove, maxHeight, } = lcgVcgTcgAndPairings;
    const missingImportantXcgs = missingImportantTcgs || missingImportantLcgs || missingImportantVcgs;
    // 1. Add missing LCGs and VCGs if needed, return x,y functions
    const coordFns = generateCoordFunctions({
        sizeSummary,
        baysData,
        vesselPartsData,
        bayLcvVcgTcgAndPairings: lcgVcgTcgAndPairings,
        maxHeightAbsolute: containersWithPositions.maxHeightAbsolute,
        svgElementMeasures,
    });
    if (!coordFns) {
        throw new Error("Could not generate coord functions");
    }
    const { xRange, yRange, xPos, yPos, scaled, oneInchScaled, allPartsLengthInMeters, svgSizeRatio, vOneBaySlots, } = coordFns;
    const { generalMedianVcg: midVcg, midVcgByBay, offsetBottomBasesDict, } = containersWithPositions;
    // 2. SVG Node
    const svgNode = svgElement.cloneNode(true);
    svgNode.setAttribute("data-rnd", Math.random().toString(36).slice(2, 7));
    if (xRange)
        svgNode.setAttribute("width", String(roundDec(xRange, 0)));
    if (yRange)
        svgNode.setAttribute("height", String(roundDec(yRange, 0)));
    if (xRange && yRange)
        svgNode.setAttribute("viewBox", `0 0 ${roundDec(xRange, 0)} ${roundDec(yRange, 0)}`);
    svgNode.setAttribute("data-scale", String(roundDec(svgSizeRatio, 5)));
    svgNode.setAttribute("data-one-feet-scaled", String(oneInchScaled));
    svgNode.setAttribute("data-length-meters", String(allPartsLengthInMeters));
    const svgSideViewNode = svgNode.querySelector("#SideView");
    const svgShellNode = svgNode.querySelector("#ShipBody");
    if (!svgShellNode || !svgSideViewNode)
        throw new Error("No SVG node found with id 'ShipBody'");
    adjustSvgShellPosition(svgShellNode);
    // 3. Add symbols
    const symbols = generateSideContainerSymbols(symbolsOptions || { skyColor: "#fff" }, svgSizeRatio, prefixSymbol);
    const symbolsNames = Object.keys(symbols);
    const symbolsTarget = svgNode.querySelector("defs") || svgNode;
    symbolsNames.forEach((key) => {
        symbolsTarget.appendChild(symbols[key]);
    });
    const belowSvgGroupsByBay = new Map();
    const paddingBoxes = scaled(300); // 0.2 meters
    // 4. Top bay numbers
    const drawTopBayNumbers = (blps) => createSvgTopBayNumbers({
        blps,
        lcgsBy20Bay,
        svgNode,
        xPos,
        fontColor: symbolsOptions && symbolsOptions.fontColor,
        fontSize: 32,
        onDoubleClick: bayNumbersDoubleClickAction,
    });
    if (bayLevelPositionsAbove.length)
        drawTopBayNumbers(bayLevelPositionsAbove);
    else
        drawTopBayNumbers(bayLevelPositionsBelow);
    let svgBayLevelGroups = [];
    if (totalSlotsCount > 0) {
        // 5. Vessel parts
        drawVesselParts(midVcg);
        // 6. Draw containers
        svgBayLevelGroups = drawSideContainers({
            bayLevelPositionsAbove,
            bayLevelPositionsBelow,
            calculatedPairedBays: pairedBays,
            offsetBottomBasesDict,
            containersWithPositions,
            minTierAbove,
            prefixSymbol,
        });
        // 7. Draw bulkheads
        drawBulkheads(xRange, midVcgByBay);
    }
    return {
        sideViewSvg: svgNode,
        missingImportantXcgs,
        xRange,
        yRange,
        svgSizeRatio,
        xPos,
        yPos,
        svgBayLevelGroups,
    };
    function adjustSvgShellPosition(svgShellNode) {
        const yScale = (scaled(midVcg) + svgElementMeasures.paddingShip.y) /
            svgElementMeasures.sideHeight;
        svgShellNode.style.transformOrigin = "0 0";
        svgShellNode.style.transformBox = "fill-box";
        svgShellNode.style.overflow = "visible";
        svgShellNode.style.transform = `matrix(1, 0, 0, ${yScale}, 0, ${svgElementMeasures.paddingShip.y + yPos(midVcg)})`;
        const svgBowTopNode = svgNode.querySelector("#BowTop");
        if (!svgBowTopNode)
            throw new Error("No SVG node found with id 'BowTop'");
        svgBowTopNode.setAttribute("transform", `matrix(1, 0, 0, 1, 0, ${-svgElementMeasures.translatedPartsY +
            svgElementMeasures.paddingShip.y +
            yPos(midVcg)})`);
        // Adjust colors
        if (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.shipTopColor) {
            const cTop = svgNode.querySelector("#_Side_Linear2");
            if (cTop) {
                const offsets = Array.from(cTop.querySelectorAll("stop[offset]"));
                const color1 = symbolsOptions.shipTopColor;
                const color0 = colorShade(color1, -20);
                const off0 = offsets.find((o) => o.getAttribute("offset") === "0");
                const off1 = offsets.find((o) => o.getAttribute("offset") === "1");
                if (off0)
                    off0.style.stopColor = color0;
                if (off1)
                    off1.style.stopColor = color1;
            }
        }
        if (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.shipBottomColor) {
            const cBottom = svgNode.querySelector("#_Side_Linear1");
            if (cBottom) {
                const offsets = Array.from(cBottom.querySelectorAll("stop[offset]"));
                const color0 = symbolsOptions.shipBottomColor;
                const color1 = colorShade(color0, 60);
                const off0 = offsets.find((o) => o.getAttribute("offset") === "0");
                const off1 = offsets.find((o) => o.getAttribute("offset") === "1");
                if (off0)
                    off0.style.stopColor = color0;
                if (off1)
                    off1.style.stopColor = color1;
            }
        }
    }
    function drawSideContainers({ bayLevelPositionsAbove, bayLevelPositionsBelow, calculatedPairedBays, offsetBottomBasesDict, containersWithPositions, minTierAbove, prefixSymbol, }) {
        const svgGroups = [];
        const belowDeckBoxes = [[]];
        // const midVcgPosY = yPos(midVcg);
        const maxDistToDrawBoxes = scaled(4000); // 4 meters
        let lastXBelowDeckBox = Infinity;
        let lastLevel = BayLevelEnum.ABOVE;
        const svgGroupShipCovers = document.createElementNS("http://www.w3.org/2000/svg", "g");
        svgGroupShipCovers.setAttribute("id", "ShipCovers");
        const svgGroupBays = document.createElementNS("http://www.w3.org/2000/svg", "g");
        svgGroupBays.setAttribute("id", "Bays");
        svgNode.appendChild(svgGroupShipCovers);
        const containersByBays = createDictionaryMultiple(selectContainersToDraw(containersWithPositions, selectContainerCriteria), (c) => c.isoOddBay);
        [...calculatedPairedBays.pairedBays, ...calculatedPairedBays.unpairedBays]
            .sort((a, b) => a.base === b.base ? a.level - b.level : Number(a.base) - Number(b.base))
            .forEach((pb) => {
            const blps = (pb.level === BayLevelEnum.ABOVE
                ? bayLevelPositionsAbove
                : bayLevelPositionsBelow).filter((blp) => pb.allBays.indexOf(blp.isoBay) >= 0);
            const isoBays = blps
                .map((b) => b.isoBay)
                .filter((b, i, a) => a.indexOf(b) === i);
            const level = blps[0].level;
            const svgGroupResp = createSvgGroup({
                isoBays,
                level,
                offsetBottomBasesDict,
                containersWithPositions,
            });
            if (!svgGroupResp)
                return;
            if (level === BayLevelEnum.BELOW) {
                belowSvgGroupsByBay.set(pb.base, svgGroupResp);
            }
            const midVcgOfThisBay = midVcgByBay[pb.base];
            const midVcgOfthisBayPosY = yPos(midVcgOfThisBay);
            if (svgGroupResp.yMax > midVcgOfthisBayPosY ||
                level === BayLevelEnum.ABOVE) {
                if (lastXBelowDeckBox - svgGroupResp.xMax > maxDistToDrawBoxes ||
                    level !== lastLevel) {
                    belowDeckBoxes.push([]);
                }
                belowDeckBoxes[belowDeckBoxes.length - 1].push({
                    xf: svgGroupResp.xMax,
                    xa: svgGroupResp.xMin,
                    yBottom: svgGroupResp.yMax,
                    yTop: svgGroupResp.yMin,
                    isAbove: level === BayLevelEnum.ABOVE,
                    bbase: pb.base,
                });
                lastXBelowDeckBox = svgGroupResp.xMin;
                lastLevel = level;
            }
            svgGroups.push(svgGroupResp);
            drawContainers(Object.assign({ isoBays,
                level,
                minTierAbove,
                containersByBays,
                prefixSymbol }, svgGroupResp));
            svgGroupBays.appendChild(svgGroupResp.svgGroup);
        });
        drawShipSpacesInShell();
        svgNode.appendChild(svgGroupBays);
        return svgGroups;
        function createSvgGroup({ isoBays, level, offsetBottomBasesDict, containersWithPositions, }) {
            var _a, _b, _c;
            const isoBaseBay = isoBays[0];
            const isoPairedBay = isoBays[1];
            const levelBaseBay = level;
            const adjustBB = (_b = (_a = offsetBottomBasesDict[`${isoBaseBay}-${levelBaseBay}`]) !== null && _a !== void 0 ? _a : offsetBottomBasesDict[`${isoPairedBay}-${levelBaseBay}`]) !== null && _b !== void 0 ? _b : 0;
            const { bayLevelCgsInfo } = containersWithPositions;
            const bayLevelInfo = (_c = bayLevelCgsInfo[isoBaseBay]) === null || _c === void 0 ? void 0 : _c[levelBaseBay];
            const maxHeight = bayLevelInfo === null || bayLevelInfo === void 0 ? void 0 : bayLevelInfo.maxHeightOfBay;
            const bottomBase = bayLevelInfo === null || bayLevelInfo === void 0 ? void 0 : bayLevelInfo.bottomBase;
            if (maxHeight === undefined || bottomBase === undefined)
                return undefined;
            const lcgs20OfBay = lcgsBy20Bay[isoBaseBay];
            const lcgs20OfPairedBay = lcgsBy20Bay[isoPairedBay];
            const aftLcgs = lcgs20OfBay ? [lcgs20OfBay.minAftLcg] : [];
            if (isoPairedBay)
                aftLcgs.push(lcgs20OfPairedBay.minAftLcg);
            const foreLcgs = lcgs20OfBay ? [lcgs20OfBay.maxForeLcg] : [];
            if (isoPairedBay)
                foreLcgs.push(lcgs20OfPairedBay.maxForeLcg);
            const xMin = xPos(Math.min(...aftLcgs), 0);
            const xMax = xPos(Math.max(...foreLcgs), 0);
            /** SVG Coordinate (lower at the top) */
            const yMax = roundDec(yPos(bottomBase + adjustBB), 0);
            /** SVG Coordinate (higher at the bottom) */
            const yMin = roundDec(yPos(bottomBase + adjustBB + maxHeight), 0);
            const svgGroup = document.createElementNS("http://www.w3.org/2000/svg", "g");
            svgGroup.classList.add("svg-group-bay-level");
            svgGroup.setAttribute("data-id", `${isoBays.join("-")}|${level}`);
            svgGroup.setAttribute("data-bbase", `${bayLevelInfo === null || bayLevelInfo === void 0 ? void 0 : bayLevelInfo.bottomBase}`);
            svgGroup.setAttribute("data-bay", isoBaseBay);
            svgGroup.setAttribute("data-height", `${maxHeight}`);
            svgGroup.setAttribute("data-size-w", `${Math.abs(roundDec(xMin - xMax))}`);
            svgGroup.setAttribute("data-size-h", `${Math.abs(roundDec(yMin - yMax))}`);
            if (missingImportantXcgs)
                svgGroup.setAttribute("data-action", `move`);
            svgGroup.setAttribute("transform", `translate(${xMin}, ${roundDec(yMin + svgElementMeasures.paddingShip.y)})`);
            return {
                svgGroup,
                xMin,
                xMax,
                yMin,
                yMax,
                yMinForGroup: yMin,
            };
        }
        /** Draws the containers of a single bay-and-level */
        function drawContainers({ isoBays, level, minTierAbove, svgGroup, prefixSymbol, xMin, yMinForGroup, containersByBays, }) {
            const fltrLevelFn = level == BayLevelEnum.ABOVE
                ? (c) => c.bottomTier !== undefined && Number(c.bottomTier) >= minTierAbove
                : (c) => c.bottomTier !== undefined && Number(c.bottomTier) < minTierAbove;
            isoBays
                .flatMap((b) => containersByBays[b] || [])
                .filter(fltrLevelFn)
                .forEach((c) => {
                if (c.lcg === undefined ||
                    c.tierBottomBase === undefined ||
                    c.meta === undefined)
                    return;
                const cellPos = `${c.meta.row}${c.meta.tier}`;
                const contSvg = drawContainerSvg(prefixSymbol, c, offsetBottomBasesDict, isoBays, level, xMin, yMinForGroup, cellPos);
                if (contSvg)
                    svgGroup.appendChild(contSvg);
            });
            svgNode.appendChild(svgGroup);
        }
        function selectContainersToDraw(containersWithPositions, selectContainerCriteria) {
            const { containerCgsByBayAndLevelAndRow, containersCgsArray } = containersWithPositions;
            const byBayAndLevelAndRowKeys = Object.keys(containerCgsByBayAndLevelAndRow).sort();
            const subsetOfContainersToDraw = [];
            switch (selectContainerCriteria) {
                case "maxSize":
                    {
                        loopOverContainers((allRowsSetExt, allTiersSetExt, pb, containerMatrixPerBayAndLevel) => {
                            var _a;
                            const allRows = Array.from(allRowsSetExt).sort(sortRowsArray); // Port to stbd
                            const allTiers = Array.from(allTiersSetExt)
                                .filter(Boolean)
                                .sort(); // Bottom to top
                            for (const tier of allTiers) {
                                const has20 = new Set();
                                let has40 = false;
                                const subsetOfContainersToDrawInTier = [];
                                loopRows: for (const row of allRows) {
                                    // Now we loop over the bays
                                    for (const bay of pb.allBays) {
                                        const infoByBayAndLevel = containerMatrixPerBayAndLevel[`${bay}-${pb.level}`];
                                        if (!infoByBayAndLevel)
                                            continue;
                                        const cont = (_a = infoByBayAndLevel[tier]) === null || _a === void 0 ? void 0 : _a[row];
                                        if (!cont)
                                            continue;
                                        if (cont.size >= 40)
                                            has40 = true;
                                        if (!has20.has(bay) || cont.size >= 40) {
                                            subsetOfContainersToDrawInTier.push(cont);
                                            if (cont.size < 40)
                                                has20.add(bay);
                                            if (cont.size >= 40)
                                                break loopRows;
                                        }
                                    }
                                }
                                subsetOfContainersToDraw.push(...subsetOfContainersToDrawInTier.filter((c) => !has40 || c.size >= 40));
                            }
                        });
                    }
                    break;
                case "all":
                    subsetOfContainersToDraw.push(...containersCgsArray.slice());
                    break;
                case "stbdOuter":
                    {
                        loopOverContainers((allRowsSetExt, allTiersSetExt, pb, containerMatrixPerBayAndLevel) => {
                            var _a;
                            const allRows = Array.from(allRowsSetExt)
                                .sort(sortRowsArray)
                                .reverse(); // Stbd to port
                            const allTiers = Array.from(allTiersSetExt)
                                .filter(Boolean)
                                .sort(); // Bottom to top
                            loopTiers: for (const tier of allTiers) {
                                const has20 = new Set();
                                loopRows: for (const row of allRows) {
                                    for (const bay of pb.allBays) {
                                        const infoByBayAndLevel = containerMatrixPerBayAndLevel[`${bay}-${pb.level}`];
                                        if (!infoByBayAndLevel)
                                            continue;
                                        const cont = (_a = infoByBayAndLevel[tier]) === null || _a === void 0 ? void 0 : _a[row];
                                        if (!cont)
                                            continue;
                                        if (!has20.has(bay) || cont.size >= 40) {
                                            subsetOfContainersToDraw.push(cont);
                                            if (cont.size < 40)
                                                has20.add(bay);
                                            if (has20.size === pb.allBays.length)
                                                continue loopTiers;
                                            if (cont.size >= 40)
                                                break loopRows;
                                        }
                                    }
                                }
                            }
                        });
                    }
                    break;
                case "portOuter":
                    {
                        loopOverContainers((allRowsSetExt, allTiersSetExt, pb, containerMatrixPerBayAndLevel) => {
                            var _a;
                            const allRows = Array.from(allRowsSetExt).sort(sortRowsArray); // Port to stbd
                            const allTiers = Array.from(allTiersSetExt)
                                .filter(Boolean)
                                .sort(); // Bottom to top
                            loopTiers: for (const tier of allTiers) {
                                const has20 = new Set();
                                loopRows: for (const row of allRows) {
                                    for (const bay of pb.allBays) {
                                        const infoByBayAndLevel = containerMatrixPerBayAndLevel[`${bay}-${pb.level}`];
                                        if (!infoByBayAndLevel)
                                            continue;
                                        const cont = (_a = infoByBayAndLevel[tier]) === null || _a === void 0 ? void 0 : _a[row];
                                        if (!cont)
                                            continue;
                                        if (!has20.has(bay) || cont.size >= 40) {
                                            subsetOfContainersToDraw.push(cont);
                                            if (cont.size < 40)
                                                has20.add(bay);
                                            if (has20.size === pb.allBays.length)
                                                continue loopTiers;
                                            if (cont.size >= 40)
                                                break loopRows;
                                        }
                                    }
                                }
                            }
                        });
                    }
                    break;
                default: {
                    const rowSelected = selectContainerCriteria;
                    const unique = new Map();
                    byBayAndLevelAndRowKeys.forEach((key) => {
                        const [, , row] = key.split("-");
                        if (row === rowSelected) {
                            const conts = containerCgsByBayAndLevelAndRow[key];
                            if (conts)
                                conts.forEach((c) => {
                                    var _a, _b;
                                    if ((_a = c.meta) === null || _a === void 0 ? void 0 : _a.position)
                                        unique.set((_b = c.meta) === null || _b === void 0 ? void 0 : _b.position, c);
                                });
                        }
                    });
                    subsetOfContainersToDraw.push(...Array.from(unique.values()));
                    break;
                }
            }
            return subsetOfContainersToDraw;
            function loopOverContainers(filterFn) {
                const subsetOfContainersToDraw = [];
                const { containerMatrixPerBayAndLevel, allTiersInBayAndLevel, allRowsInBayAndLevel, } = createContainersMatrixPerBayAndLevel();
                [
                    ...calculatedPairedBays.pairedBays,
                    ...calculatedPairedBays.unpairedBays,
                ].forEach((pb) => {
                    const allRowsSetExt = new SetEx();
                    const allTiersSetExt = new SetEx();
                    pb.allBays.forEach((bay) => {
                        const key = `${bay}-${pb.level}`;
                        const allRowsSet = allRowsInBayAndLevel.get(key);
                        const allTiersSet = allTiersInBayAndLevel.get(key);
                        if (!allRowsSet || !allTiersSet)
                            return;
                        allRowsSetExt.merge(allRowsSet);
                        allTiersSetExt.merge(allTiersSet);
                    });
                    filterFn(allRowsSetExt, allTiersSetExt, pb, containerMatrixPerBayAndLevel);
                });
                return subsetOfContainersToDraw;
            }
            function createContainersMatrixPerBayAndLevel() {
                const containerMatrixPerBayAndLevel = {};
                const allTiersInBayAndLevel = new Map();
                const allRowsInBayAndLevel = new Map();
                byBayAndLevelAndRowKeys.forEach((key) => {
                    const [bay, levelStr, row] = key.split("-");
                    if (!containerMatrixPerBayAndLevel[`${bay}-${levelStr}`])
                        containerMatrixPerBayAndLevel[`${bay}-${levelStr}`] = {};
                    const contsOfRow = containerCgsByBayAndLevelAndRow[key];
                    if (!contsOfRow)
                        return;
                    contsOfRow.forEach((c) => {
                        var _a, _b, _c;
                        const tier = (_a = c.meta) === null || _a === void 0 ? void 0 : _a.tier;
                        if (!tier)
                            return;
                        if (!containerMatrixPerBayAndLevel[`${bay}-${levelStr}`][tier])
                            containerMatrixPerBayAndLevel[`${bay}-${levelStr}`][tier] = {};
                        containerMatrixPerBayAndLevel[`${bay}-${levelStr}`][tier][row] = c;
                        if (!allTiersInBayAndLevel.has(`${bay}-${levelStr}`))
                            allTiersInBayAndLevel.set(`${bay}-${levelStr}`, new Set());
                        if (!allRowsInBayAndLevel.has(`${bay}-${levelStr}`))
                            allRowsInBayAndLevel.set(`${bay}-${levelStr}`, new Set());
                        (_b = allTiersInBayAndLevel
                            .get(`${bay}-${levelStr}`)) === null || _b === void 0 ? void 0 : _b.add(tier);
                        (_c = allRowsInBayAndLevel
                            .get(`${bay}-${levelStr}`)) === null || _c === void 0 ? void 0 : _c.add(row);
                    });
                });
                return {
                    containerMatrixPerBayAndLevel,
                    allTiersInBayAndLevel,
                    allRowsInBayAndLevel,
                };
            }
        }
        function drawShipSpacesInShell() {
            // console.log("belowDeckBoxes", belowDeckBoxes);
            belowDeckBoxes
                .filter((b) => b.length > 0)
                .forEach((b) => {
                const pathParts = [];
                let lastXa = 0;
                let topOfBox = 0;
                let bottomOfBox = 0;
                b.forEach((bb, idx2) => {
                    if (bb.isAbove) {
                        topOfBox = bb.yTop - paddingBoxes;
                        bottomOfBox = bb.yBottom + paddingBoxes;
                    }
                    else {
                        const midVcgOfThisBay = midVcgByBay[bb.bbase] || midVcg;
                        const midVcgOfthisBayPosY = yPos(midVcgOfThisBay);
                        topOfBox = roundDec(midVcgOfthisBayPosY + paddingBoxes);
                        bottomOfBox = bb.yBottom + paddingBoxes * 2;
                    }
                    if (idx2 === 0) {
                        pathParts.push(`M${roundDec(bb.xf + paddingBoxes, 0)},${roundDec(topOfBox, 0)}`);
                    }
                    pathParts.push(`L${roundDec(bb.xf + paddingBoxes, 0)},${roundDec(bottomOfBox, 0)} L${roundDec(bb.xa - paddingBoxes, 0)},${roundDec(bottomOfBox, 0)}`);
                    lastXa = roundDec(bb.xa - paddingBoxes);
                });
                pathParts.push(` L${lastXa},${topOfBox} Z`);
                const svgPath = document.createElementNS("http://www.w3.org/2000/svg", "path");
                svgPath.setAttribute("stroke", (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.strokeColor) || "red");
                svgPath.setAttribute("fill", (symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.skyColor) || "white");
                svgPath.setAttribute("stroke-width", "0");
                svgPath.setAttribute("name", `belowDeckBox`);
                svgPath.setAttribute("d", pathParts.join(" "));
                svgPath.setAttribute("transform", `translate(0, ${svgElementMeasures.paddingShip.y})`);
                svgGroupShipCovers.appendChild(svgPath);
            });
        }
        function drawContainerSvg(prefixSymbol, c, offsetBottomBasesDict, isoBays, level, xMin, yMinForGroup, cellPos) {
            var _a, _b;
            const contSvg = document.createElementNS("http://www.w3.org/2000/svg", "use");
            contSvg.setAttribute("href", `#${prefixSymbol}${c.size}-${c.extending || 0}`);
            const adjustBB = (_b = (_a = offsetBottomBasesDict[`${isoBays[0] || ""}-${level}`]) !== null && _a !== void 0 ? _a : offsetBottomBasesDict[`${isoBays[1] || ""}-${level}`]) !== null && _b !== void 0 ? _b : 0;
            if (c.lcg === undefined) {
                return;
            }
            contSvg.setAttribute("x", `${roundDec(xPos(c.lcg) - xMin, 0)}`);
            contSvg.setAttribute("y", `${roundDec(yPos((c.tierBottomBase || 0) + adjustBB) - yMinForGroup, 0)}`);
            contSvg.setAttribute("data-p", `${cellPos}`);
            contSvg.setAttribute("data-bb", `${c.bottomBase}-${c.tierBottomBase}`);
            contSvg.setAttribute("data-bay", c.isoOddBay);
            const contTitle = document.createElementNS("http://www.w3.org/2000/svg", "title");
            contTitle.innerHTML = `${c.isoOddBay}-${cellPos}`;
            contSvg.appendChild(contTitle);
            return contSvg;
        }
    }
    function drawBulkheads(xRange, midVcgByBay) {
        const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
        path.setAttribute("data-type", "bulkhead");
        path.setAttribute("stroke", "#444");
        path.setAttribute("fill", "#444");
        path.setAttribute("stroke-width", String((symbolsOptions === null || symbolsOptions === void 0 ? void 0 : symbolsOptions.strokeWidth) || 8));
        path.setAttribute("fill-opacity", "1");
        const pathParts = [];
        bayLevelPositionsBelow
            .filter((m) => m.bulkhead && (m.bulkhead.fore || m.bulkhead.aft))
            .forEach((maxByTier) => {
            const isoBaseBay = maxByTier.isoBay;
            const midVcg = midVcgByBay[isoBaseBay];
            if (maxByTier.bulkhead) {
                const hasFore = maxByTier.bulkhead.fore;
                const hasAft = maxByTier.bulkhead.aft;
                const foreLcg = lcgsBy20Bay[maxByTier.isoBay].maxForeLcg;
                const aftLcg = lcgsBy20Bay[maxByTier.isoBay].minAftLcg;
                const belowSvgGroup = belowSvgGroupsByBay.get(isoBaseBay);
                const h = belowSvgGroup
                    ? belowSvgGroup.yMax + paddingBoxes * 3
                    : scaled(midVcg);
                if (hasFore) {
                    addBulkSvgNode(foreLcg, midVcg, h);
                }
                if (hasAft) {
                    addBulkSvgNode(aftLcg, midVcg, h);
                }
            }
        });
        function addBulkSvgNode(lcg, maxV, heightSvg) {
            const BULK_WIDTH = 6;
            if (!lcg || !maxV)
                return;
            const x = xPos(lcg);
            // Removes bugs when the bulkhead is outside the ship
            if (!xRange || Math.abs(x / xRange) > 1.5)
                return;
            pathParts.push(`M${xPos(lcg)},${yPos(maxV) + svgElementMeasures.paddingShip.y} h${BULK_WIDTH} V${heightSvg + svgElementMeasures.paddingShip.y} h-${BULK_WIDTH} z`);
        }
        path.setAttribute("d", pathParts.join(" "));
        svgNode.appendChild(path);
    }
    function drawVesselParts(midVcg) {
        const midY = -svgElementMeasures.translatedPartsY +
            svgElementMeasures.paddingShip.y +
            yPos(midVcg);
        vOneBaySlots.forEach((vOneBay) => {
            if (vOneBay.type === VesselPartTypeEnum.BAY ||
                vOneBay.type === VesselPartTypeEnum.SPACER) {
                return;
            }
            const x = xPos(vOneBay.aftLcg, 2);
            switch (vOneBay.type) {
                case "BRG":
                    {
                        const aboveDeckHeight = scaled(maxHeight !== undefined ? maxHeight - midVcg : 0) * 1.65;
                        const yScale = aboveDeckHeight / svgElementMeasures.parts.bridge.width;
                        useSymbolAndTransform({
                            id: `#_Side_Bridge`,
                            x: x,
                            y: midY + svgElementMeasures.parts.bridge.width - aboveDeckHeight,
                            padX: scaled(1000), // 250mm = 0.25m L space between parts
                            padY: 0,
                            len: scaled(feetToMillimeters(vOneBay.len)),
                            yScale,
                            objToAppend: svgNode,
                            partNameInMeasures: "bridge",
                        });
                    }
                    break;
                case "SMK":
                    {
                        const aboveDeckHeight = scaled(maxHeight !== undefined ? maxHeight - midVcg : 0) * 1.2;
                        const yScale = aboveDeckHeight / svgElementMeasures.parts.smokestack.width;
                        useSymbolAndTransform({
                            id: `#_Side_SmokeStack`,
                            x: x,
                            y: midY +
                                svgElementMeasures.parts.smokestack.width -
                                aboveDeckHeight,
                            padX: scaled(250), // 250mm = 0.25m L space between parts
                            padY: 0,
                            len: scaled(feetToMillimeters(vOneBay.len)),
                            yScale,
                            objToAppend: svgNode,
                            partNameInMeasures: "smokestack",
                        });
                    }
                    break;
                case "CRN":
                    {
                        const aboveDeckHeight = scaled(maxHeight !== undefined ? maxHeight - midVcg : 0) * 1.0;
                        const yScale = aboveDeckHeight / svgElementMeasures.parts.crane.width;
                        const spaceBetweenCranes = scaled(1500);
                        const allSpaceBetweenCranes = spaceBetweenCranes * (vOneBay.cranes.length - 1) * 0.5;
                        vOneBay.cranes
                            .slice()
                            .sort()
                            .forEach((crane, idx) => {
                            const attrs = {
                                x: x - allSpaceBetweenCranes + idx * spaceBetweenCranes,
                                y: midY +
                                    svgElementMeasures.parts.crane.width -
                                    aboveDeckHeight,
                                name: crane === CraneSideEnum.PORT ? "port" : "stbd",
                            };
                            useSymbolAndTransform(Object.assign(Object.assign({ id: crane === CraneSideEnum.PORT
                                    ? `#_Side_CraneFront`
                                    : `#_Side_CraneBack` }, attrs), { transformOrigin: "0 0", padX: scaled(250), padY: 0, len: scaled(feetToMillimeters(vOneBay.len)), yScale, objToAppend: svgNode, partNameInMeasures: "crane" }));
                        });
                    }
                    break;
            }
        });
    }
    function useSymbolAndTransform({ id, x, y, padX, padY, len, yScale, objToAppend, transformOrigin = "0 0", name, partNameInMeasures, }) {
        var _a;
        const elDef = svgElement.querySelector(id);
        if (!elDef)
            throw new Error(`No SVG node found with id '${id}'`);
        const el = (_a = elDef.querySelector("g")) === null || _a === void 0 ? void 0 : _a.cloneNode(true);
        if (!el)
            throw new Error(`No SVG node G found within id '${id}'`);
        const xScale = (len - padX * 2) / svgElementMeasures.parts[partNameInMeasures].length;
        const adjustedZScale = (svgElementMeasures.parts[partNameInMeasures].width /
            (svgElementMeasures.parts[partNameInMeasures].width +
                svgElementMeasures.deckPadWidth)) *
            yScale;
        el.style.transformOrigin = transformOrigin;
        el.style.transformBox = "fill-box";
        el.style.overflow = "visible";
        el.style.transform = `matrix(${xScale}, 0, 0, ${adjustedZScale}, ${x + padX}, ${y + padY})`;
        if (name) {
            el.setAttribute("data-name", name);
        }
        objToAppend.appendChild(el);
    }
}
// interface SvgPath {
//   (path: string): SvgPath;
//   from(path: string | SvgPath): SvgPath;
//   abs(): SvgPath;
//   rel(): SvgPath;
//   scale(sx: number, sy?: number): SvgPath;
//   translate(x: number, y?: number): SvgPath;
//   rotate(angle: number, rx?: number, ry?: number): SvgPath;
//   skewX(degrees: number): SvgPath;
//   skewY(degrees: number): SvgPath;
//   matrix(m: number[]): SvgPath;
//   transform(str: string): SvgPath;
//   unshort(): SvgPath;
//   unarc(): SvgPath;
//   toString(): string;
//   round(precision: number): SvgPath;
//   iterate(
//     iterator: (segment: unknown, index: number, x: number, y: number) => void,
//     keepLazyStack?: boolean,
//   ): SvgPath;
// }
// function applyTransformToPathsRecursively(
//   el: SVGElement,
//   xScale: number,
//   yScale: number,
//   translateX: number,
//   translateY: number,
//   applyTransformToPaths: SvgPath,
// ): void {
//   switch (el.nodeName) {
//     case "circle":
//     case "ellipse":
//       el.setAttribute(
//         "cx",
//         String(roundDec(Number(el.getAttribute("cx")) + translateX, 0)),
//       );
//       el.setAttribute(
//         "cy",
//         String(roundDec(Number(el.getAttribute("cy")) + translateY, 0)),
//       );
//       break;
//     case "rect":
//       el.setAttribute(
//         "x",
//         String(roundDec(Number(el.getAttribute("x")) + translateX, 0)),
//       );
//       el.setAttribute(
//         "y",
//         String(roundDec(Number(el.getAttribute("y")) + translateY, 0)),
//       );
//       el.setAttribute(
//         "width",
//         String(roundDec(Number(el.getAttribute("width")) * xScale, 0)),
//       );
//       el.setAttribute(
//         "height",
//         String(roundDec(Number(el.getAttribute("height")) * yScale, 0)),
//       );
//       break;
//     case "use":
//       el.setAttribute(
//         "x",
//         String(roundDec(Number(el.getAttribute("x")) + translateX, 0)),
//       );
//       el.setAttribute(
//         "y",
//         String(roundDec(Number(el.getAttribute("y")) + translateY, 0)),
//       );
//       break;
//     case "path":
//       {
//         const d = el.getAttribute("d");
//         if (!d) return;
//         const path = applyTransformToPaths(d)
//           .scale(xScale, yScale)
//           .translate(translateX, translateY);
//         if (path) {
//           el.setAttribute("d", path.toString());
//         }
//       }
//       break;
//     case "g": {
//       // For <g> elements, we don't need to do anything special
//       // as the transformation will be applied to its children.
//       Array.from(el.children).forEach((child) => {
//         applyTransformToPathsRecursively(
//           child as SVGElement,
//           xScale,
//           yScale,
//           translateX,
//           translateY,
//           applyTransformToPaths,
//         );
//       });
//       break;
//     }
//   }
//   // const d = el.nodeName === "path" ? el.getAttribute("d") : null;
//   // if (d) {
//   //   const path = applyTransformToPaths(d)
//   //     .scale(xScale, yScale)
//   //     .translate(translateX, translateY);
//   //   if (path) {
//   //     el.setAttribute("d", path.toString());
//   //   }
//   // }
//   // Array.from(el.children).forEach((child) => {
//   //   applyTransformToPathsRecursively(
//   //     child as SVGElement,
//   //     xScale,
//   //     yScale,
//   //     translateX,
//   //     translateY,
//   //     applyTransformToPaths,
//   //   );
//   // });
// }
